"use client";
import { useRef, useState } from "react";
import clsx from "clsx";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";

import s from "./styles.module.css";

const CONTROLLER_WIDTH = 140;

interface ControllerProps {
  total: number;
  current: number;
  className?: string;
}

gsap.registerPlugin(useGSAP, ScrollTrigger);

export const Controller: React.FC<ControllerProps> = ({
  total,
  current,
  className,
}) => {
  const THUMB_WIDTH = 40;
  const trackWidth = CONTROLLER_WIDTH - THUMB_WIDTH;
  const thumbPosition = (current / total) * trackWidth;

  return (
    <div
      className={clsx(s.controllerContainer, className)}
      style={{ width: `${CONTROLLER_WIDTH}px` }}
    >
      <div className={s.controller} style={{ width: `${CONTROLLER_WIDTH}px` }}>
        <div
          className={s.thumbWrapper}
          style={{ width: `${CONTROLLER_WIDTH}px` }}
        >
          <div
            className={s.thumb}
            style={{
              width: `${THUMB_WIDTH}px`,
              transform: `translateX(${thumbPosition}px)`,
            }}
          />
        </div>
      </div>
    </div>
  );
};

interface SliderProps {
  elements: React.ReactNode[];
  showController?: boolean;
  width: number;
  regularPadding?: boolean;
  start?: string;
}

export default function Slider({
  elements,
  showController = true,
  width,
  regularPadding = true,
  start,
}: SliderProps) {
  const [progress, setProgress] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const sliderRef = useRef<HTMLDivElement>(null);
  useGSAP(
    () => {
      if (!sliderRef.current || !containerRef.current) {
        return;
      }
      const containerHeight = containerRef.current.clientHeight;
      const offset = containerHeight / 2;

      const gaps = 10 * elements.length;
      const contentWidth = width + gaps;

      gsap.set(sliderRef.current, {
        width: `${contentWidth}px`,
      });

      const slideWidth = width / elements.length;

      const adjustedX = -(contentWidth - slideWidth);

      const animation = gsap.to(sliderRef.current, {
        x: adjustedX,
        ease: "none",
      });

      ScrollTrigger.create({
        animation: animation,
        trigger: containerRef.current,
        start: start || `top center-=${offset}px`,
        end: () => `+=${contentWidth * 2}`,
        scrub: 1,
        pin: true,
        anticipatePin: 1,
        onUpdate: (self) => setProgress(self.progress),
        // markers: true, // debug
      });

      return () => {
        ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
      };
    },
    { scope: containerRef }
  );

  return (
    <div ref={containerRef} className={s.wrapper}>
      <div className={s.container}>
        <div
          ref={sliderRef}
          className={clsx(s.slider, regularPadding && s.sliderPaddingLeft)}
        >
          {elements.map((elem, i) => (
            <div key={i} className={s.slide}>
              {elem}
            </div>
          ))}
        </div>
        {showController && (
          <div className={s.controllerWrapperFromSlider}>
            <Controller total={1} current={progress} />
          </div>
        )}
      </div>
    </div>
  );
}
